import { useState, useCallback } from "react";

export interface MeditationPreset {
  id: string;
  name: string;
  durationMinutes: number;
  intervalMinutes: number; // 0 = kein Intervall
}

const STORAGE_KEY = "vajra-meditation-presets";
const MAX_PRESETS = 10;

const DEFAULT_PRESETS: MeditationPreset[] = [
  { id: "default-1", name: "Kurz", durationMinutes: 10, intervalMinutes: 0 },
  { id: "default-2", name: "Mittel", durationMinutes: 20, intervalMinutes: 0 },
  { id: "default-3", name: "Lang", durationMinutes: 45, intervalMinutes: 0 },
];

function load(): MeditationPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PRESETS;
    const parsed = JSON.parse(raw) as MeditationPreset[];
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_PRESETS;
  } catch {
    return DEFAULT_PRESETS;
  }
}

function save(presets: MeditationPreset[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
}

export function useMeditationPresets() {
  const [presets, setPresets] = useState<MeditationPreset[]>(load);

  const addPreset = useCallback((preset: Omit<MeditationPreset, "id">) => {
    setPresets((prev) => {
      const updated = [
        ...prev.slice(0, MAX_PRESETS - 1),
        { ...preset, id: crypto.randomUUID() },
      ];
      save(updated);
      return updated;
    });
  }, []);

  const removePreset = useCallback((id: string) => {
    setPresets((prev) => {
      const updated = prev.filter((p) => p.id !== id);
      save(updated);
      return updated;
    });
  }, []);

  const updatePreset = useCallback((id: string, changes: Partial<Omit<MeditationPreset, "id">>) => {
    setPresets((prev) => {
      const updated = prev.map((p) => (p.id === id ? { ...p, ...changes } : p));
      save(updated);
      return updated;
    });
  }, []);

  return { presets, addPreset, removePreset, updatePreset, maxPresets: MAX_PRESETS };
}
